import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import apiClient from "../api/client";

interface AlertRow {
  id: string;
  rule_id: string;
  entity: string | null;
  severity: string;
  status: string;
  summary: string;
  detail: Record<string, unknown> | null;
  fired_at: string | null;
  last_seen_at: string | null;
  resolved_at: string | null;
  occurrence_count: number;
  notified_channels: string[] | null;
}

function severityChip(severity: string) {
  const color = severity === "critical" ? "error" : severity === "warning" ? "warning" : "default";
  return <Chip label={severity.toUpperCase()} color={color as any} size="small" />;
}

function statusChip(status: string) {
  return (
    <Chip
      label={status === "firing" ? "FIRING" : "Resolved"}
      color={status === "firing" ? "error" : "success"}
      variant={status === "firing" ? "filled" : "outlined"}
      size="small"
    />
  );
}

/** Alerts page — operational view of the alerting engine (Wave 2.1 UI). */
export default function Alerts() {
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [evaluating, setEvaluating] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (!showResolved) params.set("status", "firing");
      const response = await apiClient.get(`/api/v1/alerts?${params}`);
      setAlerts(response.data.alerts || []);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch alerts");
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => {
    fetchAlerts();
    // Auto-refresh while there are firing alerts
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const runEvaluation = async () => {
    setEvaluating(true);
    try {
      await apiClient.post("/api/v1/alerts/evaluate");
      await fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Evaluation failed");
    } finally {
      setEvaluating(false);
    }
  };

  const resolveAlert = async (id: string) => {
    try {
      await apiClient.post(`/api/v1/alerts/${id}/resolve`);
      await fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Resolve failed");
    }
  };

  const firingCount = useMemo(() => alerts.filter((a) => a.status === "firing").length, [alerts]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Operational alerts from the built-in rule engine (dependencies, agent
            heartbeats, disk, balances, ASR). Checks run every 60s.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant={showResolved ? "contained" : "outlined"}
            size="small"
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved ? "Hiding nothing" : "Show resolved"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchAlerts}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<NotificationsActiveIcon />}
            onClick={runEvaluation}
            disabled={evaluating}
          >
            {evaluating ? "Evaluating…" : "Run checks now"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {!loading && !error && alerts.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <NotificationsActiveIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {showResolved ? "No alerts recorded" : "No firing alerts"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {showResolved
              ? "The rule engine has not recorded any incidents yet."
              : "All checks are passing. Use “Run checks now” for an immediate evaluation."}
          </Typography>
        </Paper>
      )}

      {(alerts.length > 0 || loading) && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell>Notified</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{statusChip(a.status)}</TableCell>
                  <TableCell>{severityChip(a.severity)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {a.rule_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{a.entity ?? "—"}</TableCell>
                  <TableCell>
                    <Tooltip title={a.detail ? JSON.stringify(a.detail) : ""}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 340 }}>
                        {a.summary}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{a.occurrence_count}</TableCell>
                  <TableCell>
                    {a.last_seen_at ? new Date(a.last_seen_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    {a.notified_channels?.length ? a.notified_channels.join(", ") : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {a.status === "firing" && (
                      <Button size="small" onClick={() => resolveAlert(a.id)}>
                        Resolve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        {total} alert{total === 1 ? "" : "s"} total
        {firingCount > 0 ? ` · ${firingCount} firing` : " · none firing"}
        {" · auto-refresh 30s"}
      </Typography>
    </Box>
  );
}
